<template>
    <!-- PRODUCT-26110 Task 12b (INSTANCE_ATTRS_CLASS_STYLE): Vue 3 folds class/style
         into $attrs and stops auto-applying them to root when inheritAttrs is false,
         so a caller's `class=` (e.g. StatusPage.vue's "StatusPage--terminated-request")
         would silently stop reaching this component's real root. Bind them
         explicitly to keep the old Vue 2 fallthrough behavior. -->
    <SingleCol
        :footer-stretch-x="stretch"
        :is-chat-available="hasInsuranceQuestion"
        :class="$attrs.class"
        :style="$attrs.style"
    >
        <div v-if="isMobile && !wordmarkUri" class="InterviewCard" :class="{ 'InterviewCard--stretch': stretch }">
            <div class="InterviewCard__body">
                <slot />
            </div>
        </div>
        <div v-else class="InterviewCard" :class="{ 'InterviewCard--stretch': stretch }">
            <div class="InterviewCard__header" :style="headerStyles">
                <div v-if="wordmarkUri" class="InterviewCard__wordmark">
                    <img :src="wordmarkUri" :alt="dbaNameText">
                </div>
                <template v-if="isMobile">
                    &nbsp;
                </template>
                <div v-else class="InterviewCard__message" :style="messageStyles">
                    <template v-if="showsRelyingPartyName">
                        <var>{{ dbaNameText }}</var> {{ requestingInformationText }}
                    </template>
                    <template v-else>
                        {{ headingText }}
                    </template>
                </div>
                <div v-if="isUserInGoogleFlow" class="InterviewCard__logout">
                    <LogoutButton :email="user.email" />
                    <Icon name="caret-down" />
                </div>
            </div>
            <div class="InterviewCard__body">
                <slot />
            </div>
            <div>
                <slot name="contactFooter" />
            </div>
        </div>
        <template #footer>
            <slot name="footer" />
        </template>
    </SingleCol>
</template>

<script lang="ts">
    import { defineAsyncComponent } from 'vue';

    import { Component, Prop, Vue } from 'vue-property-decorator';

    import Icon from '@/components/common/Icon.vue';
    import { getBrandingColor } from '@/config/brandingColor';
    import support from '@/config/support';
    import { isGlsSession } from '@/interfaces/SessionData';
    import { useIdoStore } from '@/store/store';

    import SingleCol from './SingleCol.vue';

    @Component({
        inheritAttrs: false,
        components: {
            LogoutButton: defineAsyncComponent(() => import('@/components/user/LogoutButton.vue')),
            SingleCol,
            Icon,
        },
    })
    export default class InterviewCard extends Vue {
        @Prop({ type: Boolean, default: support.isMobileBrowser })
        private isMobile!: boolean;

        @Prop({ type: Boolean, default: false })
        private stretch!: boolean;

        @Prop({ type: String, default: null })
        private title!: string | null;

        private get done() {
            return useIdoStore().questions
                .reduce((acc, question) => acc && question.complete, true);
        }

        private get issuer() {
            return useIdoStore().issuer;
        }

        private get user() {
            return useIdoStore().user;
        }

        private readonly requestingInformationText = 'is requesting information from you';

        /** Whether the heading is the branding sentence that names the relying party.
         *
         * The name is rendered separately from the sentence so it can sit in its own
         * element -- Weglot excludes `var` from translation, and a company name spliced
         * into the string could not be excluded at all.
         */
        private get showsRelyingPartyName(): boolean {
            return !this.done && !this.title && !this.wordmarkUri;
        }

        private get headingText() {
            if (this.done) {
                return 'Your information has been submitted';
            }
            return this.title || this.requestingInformationText;
        }

        private get dbaNameText() {
            return this.issuer?.displayName || '{{COMPANY}}';
        }

        private get brandingColor(): string {
            return getBrandingColor(this.issuer?.primaryColor);
        }

        private get wordmarkUri() {
            return this.issuer?.wordmarkUri || null;
        }

        private get isUserInGoogleFlow() {
            return Boolean(this.user && useIdoStore().session && isGlsSession(useIdoStore().session));
        }

        private get headerStyles() {
            return `border-bottom: 3px solid ${this.brandingColor}`;
        }

        private get messageStyles() {
            return `text-align: ${this.done ? 'right' : 'left'}`;
        }

        private get hasInsuranceQuestion() {
            const questions = useIdoStore()?.questions || [];
            const attributeTypes = useIdoStore().attributeSharingAgreements
                ? useIdoStore().attributeSharingAgreements.map((asa) => asa.attribute_type)
                : questions.map((question) => question.attrType);
            return attributeTypes.some((attrType) => this.isInsuranceAttribute(attrType));
        }

        private isInsuranceAttribute(attribute: string): boolean {
            return Boolean(attribute.match(/risk_decisioning\.insurance_policy_verification\.self_attestation\.([^.]+).*\.(enterprise|advanced)/));
        }
    }
</script>
